import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en/translation.json';
import ar from './locales/ar/translation.json';

/**
 * Language packs.
 *
 * English and Arabic ship inside the bundle (the appliance runs air-gapped, so
 * nothing can be fetched for the default experience). Any other language is
 * added in one of two ways, neither of which needs a dashboard code change:
 *
 *  1. Bundled: create `src/i18n/locales/<code>/translation.json`, then add one
 *     `BUNDLED` entry below and rebuild.
 *  2. Runtime: place `<code>.json` under the dashboard's `public/locales/`
 *     (for example on the appliance image). The file holds a `_meta` block
 *     with `name`, `nativeName` and `dir`, plus the translations. It is
 *     fetched the first time a user selects that language.
 *
 * Missing keys fall back to English, so a partial pack still works.
 */

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageMeta {
  code: string;
  name: string;
  nativeName: string;
  dir: TextDirection;
}

type PackResources = Record<string, unknown>;

interface BundledPack {
  meta: LanguageMeta;
  resources: PackResources;
}

const BUNDLED: BundledPack[] = [
  {
    meta: { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
    resources: en,
  },
  {
    meta: { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
    resources: ar,
  },
];

const LANGUAGE_STORAGE_KEY = 'ztai-lang';
const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/;

const registry = new Map<string, LanguageMeta>();

// Bundled packs reach i18next through init() below, so only their metadata is registered here.
BUNDLED.forEach((pack) => registry.set(pack.meta.code, pack.meta));

i18n.use(initReactI18next).init({
  resources: Object.fromEntries(
    BUNDLED.map((pack) => [pack.meta.code, { translation: pack.resources }])
  ),
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: BUNDLED.map((pack) => pack.meta.code),
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Languages whose packs are available now (bundled or already fetched). */
export const availableLanguages = (): LanguageMeta[] => Array.from(registry.values());

export const getLanguageMeta = (code: string): LanguageMeta | undefined => registry.get(code);

/** Returns the text direction for a language code. Unknown codes are left-to-right. */
export const directionOf = (code: string): TextDirection => registry.get(code)?.dir ?? 'ltr';

/**
 * Makes sure a language pack is loaded. Bundled packs are already present. For
 * any other code, the runtime pack at `public/locales/<code>.json` is fetched and
 * registered. Rejects if the code is malformed or no pack can be found.
 */
export const loadLanguage = async (code: string): Promise<void> => {
  if (registry.has(code)) return;
  if (!LANGUAGE_CODE_PATTERN.test(code)) {
    throw new Error(`Invalid language code: ${code}`);
  }
  const base = process.env.PUBLIC_URL || '';
  const response = await fetch(`${base}/locales/${code}.json`);
  if (!response.ok) {
    throw new Error(`No language pack for ${code}`);
  }
  const { _meta, ...resources } = (await response.json()) as PackResources & { _meta?: Partial<LanguageMeta> };
  if (!_meta?.name || !_meta.nativeName || (_meta.dir !== 'ltr' && _meta.dir !== 'rtl')) {
    throw new Error(`Language pack ${code} is missing a valid _meta block`);
  }
  const meta: LanguageMeta = { code, name: _meta.name, nativeName: _meta.nativeName, dir: _meta.dir };
  registry.set(code, meta);
  i18n.addResourceBundle(code, 'translation', resources, true, true);
};

/** Language saved by the user, else the browser language if we have a pack for it, else English. */
export const resolveInitialLanguage = (): string => {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && registry.has(saved)) return saved;
  } catch {
    // Storage can be blocked (private window). Fall through to the browser language.
  }
  const browser = (navigator.language || 'en').split('-')[0];
  return registry.has(browser) ? browser : 'en';
};

export const saveLanguage = (code: string) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // Non-critical: the choice just will not survive a reload.
  }
};

export default i18n;
